import React, { useState, useEffect } from "react";
import Header from "../Headers/HeaderProfile";
import Sidebar from "../Usables/Sidebar";
import useAuth from "../../hooks/useAuth";
import { useLoader } from "../../context/LoaderContext";
import axios from "../../api/axios";
import "./NewProfile.css";

// Import components for different education levels
import ProfileEducationSection from "./ProfileEducationSection";
import ProfilePersonalSection from "./ProfilePersonalSection";

const NewProfile = () => {
  const { auth } = useAuth();
  const { setIsLoading } = useLoader();
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const loadUserData = async () => {
      setIsLoading(true);

      try {
        // First check if we have data in localStorage
        const storedUserData = localStorage.getItem("userData");

        if (storedUserData) {
          const parsedData = JSON.parse(storedUserData);

          // Verify this is the correct user's data
          if (parsedData.userID === auth?.userID) {
            // Debug the education data
            if (parsedData.education) {
              console.log(
                "College name:",
                parsedData.education.collegename,
                "Type:",
                typeof parsedData.education.collegename
              );
              console.log(
                "College year:",
                parsedData.education.collegeyear,
                "Type:",
                typeof parsedData.education.collegeyear
              );
              console.log(
                "10th Board:",
                parsedData.education.tenthBoard,
                "Type:",
                typeof parsedData.education.tenthBoard
              );
              console.log(
                "12th Board:",
                parsedData.education.twelthBoard,
                "Type:",
                typeof parsedData.education.twelthBoard
              );
            }
            console.log(
              "Gender:",
              parsedData.gender,
              "Type:",
              typeof parsedData.gender
            );

            setUserData(parsedData);
            setIsLoading(false);
            return;
          }
        }

        // If we don't have data in localStorage or it's for a different user, fetch from server
        const userDetail = await axios.get("/api/getuserdata", {
          params: { userID: auth?.userID },
        });

        const authDetail = await axios.get("/api/getauthdata", {
          params: { userID: auth?.userID },
        });

        // Based on the user's category, fetch additional educational details
        const educationData = await fetchEducationData(
          userDetail?.data?.category,
          auth?.userID
        );

        // Debug server response data
        if (educationData) {
          console.log(
            "SERVER - College name:",
            educationData.collegename,
            "Type:",
            typeof educationData.collegename
          );
          console.log(
            "SERVER - College year:",
            educationData.collegeyear,
            "Type:",
            typeof educationData.collegeyear
          );
          console.log(
            "SERVER - 10th Board:",
            educationData.tenthBoard,
            "Type:",
            typeof educationData.tenthBoard
          );
          console.log(
            "SERVER - 12th Board:",
            educationData.twelthBoard,
            "Type:",
            typeof educationData.twelthBoard
          );
        }
        console.log(
          "SERVER - Gender:",
          userDetail?.data?.gender,
          "Type:",
          typeof userDetail?.data?.gender
        );

        // Combine all data into one object
        const combinedData = {
          ...userDetail.data,
          auth: authDetail.data,
          education: educationData,
          userID: auth?.userID,
        };

        // Store the complete unmodified data
        localStorage.setItem("userData", JSON.stringify(combinedData));

        setUserData(combinedData);
      } catch (error) {
        console.error("Error loading user data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (auth?.userID) {
      loadUserData();
    }
  }, [auth?.userID, setIsLoading]);

  // Helper function to fetch education data based on category
  const fetchEducationData = async (category, userID) => {
    if (!category || !userID) return null;

    let endpoint;

    // Determine which API to call based on education category
    switch (category.toLowerCase()) {
      case "below8th":
        endpoint = "/api/getBelow8thUser";
        break;
      case "9-10th":
        endpoint = "/api/getNineTenthUser";
        break;
      case "11-12th":
        endpoint = "/api/getElevenTwelthUser";
        break;
      case "bachelors":
        endpoint = "/api/getBachelorsUser";
        break;
      case "masters":
        endpoint = "/api/getMastersUser";
        break;
      case "phd":
        endpoint = "/api/getPhdUser";
        break;
      default:
        return null;
    }

    try {
      const response = await axios.get(endpoint, {
        params: { userID },
      });

      return {
        ...response.data,
        categoryType: category,
      };
    } catch (error) {
      console.error(`Error fetching ${category} data:`, error);
      return null;
    }
  };

  // Helper function to display label-value pairs appropriately
  const displayLabelValue = (item) => {
    if (item === null || item === undefined) return "Not specified";

    // If it's already a string, return it
    if (typeof item === "string") return item;

    // If it's a number, return as string
    if (typeof item === "number") return String(item);

    // If it's an object with a label property, return the label
    if (item && typeof item === "object") {
      if (item.label) return item.label;

      // If it has a value property, try to use that
      if (item.value !== undefined) return String(item.value);

      // Special case for dates
      if (item instanceof Date) return item.toLocaleDateString();
    }

    // If it's an array of objects with labels, extract and join them
    if (Array.isArray(item)) {
      if (item.length === 0) return "None selected";

      return (
        item
          .map((val) => {
            if (!val) return null;
            if (typeof val === "string") return val;
            if (typeof val === "number") return String(val);
            if (typeof val === "object") {
              if (val.label) return val.label;
              if (val.value !== undefined) return String(val.value);
            }
            return String(val);
          })
          .filter(Boolean)
          .join(", ") || "None selected"
      );
    }

    // Default fallback for unknown formats
    try {
      return JSON.stringify(item);
    } catch (e) {
      console.error("Failed to stringify item:", e);
      return "Invalid data format";
    }
  };

  return (
    <div className="new-profile-container">
      <div className="new-profile-header">
        <Header />
      </div>

      <div className="new-profile-content">
        <div className="new-profile-sidebar">
          <Sidebar />
        </div>

        <div className="new-profile-main">
          {!userData ? (
            <div className="new-profile-loading">
              <h2>Loading profile data...</h2>
            </div>
          ) : (
            <>
              <div className="user-profile-card user-profile-header-card">
                <h1>
                  {userData.firstname || "No Name"} {userData.lastname || ""}
                </h1>
                <p className="user-profile-subtitle">
                  {userData.category || userData.userType || "Student"}
                </p>
                {userData.auth && (
                  <div className="user-profile-contact">
                    <p>
                      <strong>
                        Email : {displayLabelValue(userData.auth.email)}
                      </strong>
                    </p>
                    <p>
                      <strong>
                        Phone : {displayLabelValue(userData.auth.phno)}
                      </strong>
                    </p>
                    <p>
                      <strong>
                        User ID: {displayLabelValue(userData.userID)}
                      </strong>
                    </p>
                  </div>
                )}
              </div>

              <ProfilePersonalSection
                userData={userData}
                displayLabelValue={displayLabelValue}
              />

              {userData.education ? (
                <ProfileEducationSection
                  educationData={userData.education}
                  categoryType={
                    userData.category ||
                    (userData.education && userData.education.categoryType) ||
                    "Unknown"
                  }
                  displayLabelValue={displayLabelValue}
                />
              ) : (
                <div className="user-profile-card">
                  <div className="user-profile-card-header">
                    <h2>Educational Information</h2>
                  </div>
                  <div className="user-profile-card-content">
                    <p>No educational data available</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewProfile;
